import { Theme, Typography, Checkbox, Tooltip, Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Fragment, useState } from "react";
import { SystemProps } from "@mui/system";
import { GenericModel } from "./GenericTable";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { darkGrey, primaryColor } from "../../core/theme";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import GenericHeaderCell from "./GenericHeaderCell";
import { If } from "./If";
import { GenericTableRowTooltip } from "./GenericTableRowTooltip";

export interface ColModel<TModel> {
	id: string;
	noTooltip?: boolean;
	header: () => any;
	filterBy?: boolean;
	sortable?: boolean;
	xs?: number;
	align?: string;
	sx?: SystemProps<Theme>;
	accessor: (model: TModel, index: number) => any;
	extractData?: (model: TModel, index: number) => string;
	hidden?: boolean;
	updateSortParam?: (sort: { sortBy: string; order: "asc" | "desc" }) => void;
	sortParam?: { sortBy: string; order: "asc" | "desc" };
	kind?: "text" | "link" | "datetime";
}

export interface ColSubModel<TSubModel> {
	id: string;
	header: () => any;
	filterBy?: boolean;
	sortable?: boolean;
	xs?: number;
	align?: string;
	sx?: SystemProps<Theme>;
	accessor: (model: TSubModel, index: number, parent?: TSubModel) => any;
	extractData?: (model: TSubModel) => string;
	hidden?: boolean;
	kind?: "text" | "link" | "datetime";
}

export enum TriggerUnfoldType {
	Icon,
	Click,
}

const style: any = {
	tableCell: {
		display: "flex",
		alignItems: "center",

		"& h6": {
			// width: "80%",
		},
		"& div": {
			display: "flex",
			alignItems: "center",
		},
		paddingLeft: "0px",
		"& svg, & img": {
			float: "left",
			padding: "2px 5px 0 0",
		},
	},
};

function GenericTableRow<
	TModel extends GenericModel,
	TSubModel extends GenericModel
>(props: {
	isSelected: boolean;
	isSelectable?: boolean;
	isRowSelectable?: (dataModel: TModel) => boolean;
	data: TModel;
	cols: ColModel<TModel>[];
	subCols?: ColSubModel<TSubModel>[];
	handleRowSelection: (data: TModel, selected: boolean) => void;
	index: number;
	displaySubRowheader?: boolean;
	triggerUnfoldBy?: TriggerUnfoldType.Click | TriggerUnfoldType.Icon;
	keyExtractor?: (entry: TModel) => string | number;
	itemName?: string;
}) {
	const {
		data,
		cols,
		subCols,
		isSelectable,
		isSelected,
		handleRowSelection,
		isRowSelectable = (dataModel: TModel) => !!dataModel,
		displaySubRowheader,
		triggerUnfoldBy,
		itemName,
		keyExtractor,
	} = props;
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Fragment>
				{isSelectable && (
					<Grid key={-1} item xs={0.3} sx={style.tableCell}>
						<If condition={isRowSelectable(data)}>
							<Checkbox
								data-testid={
									"check-" +
									itemName +
									"-" +
									(keyExtractor ? keyExtractor(data) : "")
								}
								checked={isSelected}
								onChange={() => {
									handleRowSelection(data, !isSelected);
								}}
							/>
						</If>
						<If condition={!isRowSelectable(data)}>
							<Box component={"span"} sx={{ padding: "9px" }}>
								<Tooltip
									sx={{
										marginLeft: "3px",
										marginTop: "-3px",
									}}
									title={
										("missing_data" in data
											? (
													data.missing_data as string
											  ).slice(0, -2)
											: "") + " not found."
									}
								>
									<HelpOutlineIcon
										sx={{ color: darkGrey }}
									></HelpOutlineIcon>
								</Tooltip>
							</Box>
						</If>
					</Grid>
				)}
				<Grid key={0} item xs={0.3} sx={style.tableCell}>
					{"children" in data &&
						triggerUnfoldBy == TriggerUnfoldType.Icon && (
							<IconButton
								aria-label="expand row"
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									setOpen(!open);
								}}
							>
								{open ? (
									<KeyboardArrowDownIcon
										sx={{ color: primaryColor }}
									/>
								) : (
									<KeyboardArrowRightIcon
										sx={{ color: primaryColor }}
									/>
								)}
							</IconButton>
						)}
				</Grid>

				{cols.map((col: ColModel<TModel>, i) => (
					<Fragment key={i}>
						{!col.hidden && (
							<Grid
								key={i}
								item
								xs={col.xs}
								sx={{
									...style.tableCell,
									minHeight: "44px",
									maxWidth: "100%",
									cursor:
										triggerUnfoldBy ==
											TriggerUnfoldType.Click &&
										"children" in data
											? "pointer"
											: "",
								}}
								onClick={() =>
									triggerUnfoldBy ==
										TriggerUnfoldType.Click &&
									"children" in data
										? setOpen(!open)
										: ""
								}
							>
								{col.noTooltip &&
									col.accessor(data, props.index)}
								{!col.noTooltip && (
									<GenericTableRowTooltip
										value={col.accessor(data, props.index)}
										data={col.extractData?.(
											data,
											props.index
										)}
										kind={col.kind}
										sx={{
											alignSelf: "center",
											textOverflow: "ellipsis",
											overflow: "hidden",
											whiteSpace: "nowrap",
											width: "100%",
										}}
									/>
								)}
							</Grid>
						)}
					</Fragment>
				))}
			</Fragment>
			{open && (
				<Fragment>
					{displaySubRowheader && (
						<Grid key={0} item xs={0.6} sx={style.tableCell}>
							<Typography
								paragraph
								sx={{ marginBottom: "40px" }}
							/>
						</Grid>
					)}
					{displaySubRowheader &&
						subCols?.map((headerCell: ColModel<TSubModel>) => (
							<GenericHeaderCell
								key={headerCell.id}
								{...headerCell}
							/>
						))}

					{"children" in data &&
						data.children &&
						data.children.map((m: GenericModel, j) => (
							<Fragment key={j}>
								{triggerUnfoldBy == TriggerUnfoldType.Icon &&
									isSelectable && (
										<Grid
											key={`sub-1`}
											item
											xs={0.3}
											sx={{
												...style.tableCell,
												minHeight: "44px",
											}}
										>
											<Typography
												paragraph
												sx={{ marginBottom: "40px" }}
											/>
										</Grid>
									)}
								<Grid
									key={`sub-0`}
									item
									xs={0.3}
									sx={style.tableCell}
								>
									<Typography
										paragraph
										sx={{ marginBottom: "40px" }}
									/>
								</Grid>
								{subCols?.map(
										(col: ColSubModel<TSubModel>, i) => (
											<Fragment key={`sub-${i}`}>
												{!col.hidden && (
													<Grid
														key={i}
														item
														xs={col.xs}
														sx={style.tableCell}
													>
														<GenericTableRowTooltip
															value={col.accessor(
																m as TSubModel,
																props.index,
																data as any
															)}
															kind={col.kind}
															data={col.extractData?.(
																m as TSubModel,
															)}
															sx={{
																alignSelf:
																	"center",
																textOverflow:
																	"ellipsis",
																overflow:
																	"hidden",
																whiteSpace:
																	"nowrap",
																width: "100%",
															}}
														/>
													</Grid>
												)}
											</Fragment>
										)
									)}
							</Fragment>
						))}
				</Fragment>
			)}
		</>
	);
}

export default GenericTableRow;
